import React from "react";
import { motion } from "framer-motion";
import { useInView } from "hooks/useInView";
import { Link2, Settings, Shield } from "lucide-react";

const steps = [
  {
    icon: Link2,
    number: "01",
    title: "Connect",
    description:
      "Link your Facebook Pages and Instagram accounts in seconds with our secure OAuth integration.",
  },
  {
    icon: Settings,
    number: "02",
    title: "Configure",
    description:
      "Set up moderation rules and customize your smart automation system.",
  },
  {
    icon: Shield,
    number: "03",
    title: "Moderate",
    description:
      "Sit back and watch Moujib handle comments and engagement automatically.",
  },
];

const HowItWorks = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <section
      id="how-it-works"
      className="py-24 bg-gradient-to-b from-background to-secondary/20 relative overflow-hidden"
    >
      {/* Decorative blob */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full blur-[100px] pointer-events-none" />

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <h2 className="text-4xl lg:text-5xl font-heading font-bold mb-4">
            Get started in{" "}
            <span className="bg-gradient-primary bg-clip-text text-transparent">
              3 simple steps
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            From zero to fully automated in minutes
          </p>
        </motion.div>

        <div ref={ref} className="relative max-w-5xl mx-auto">
          {/* Connecting line */}
          <div className="hidden lg:block absolute top-20 left-0 right-0 h-1">
            <motion.div
              initial={{ scaleX: 0 }}
              animate={inView ? { scaleX: 1 } : { scaleX: 0 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="h-full bg-gradient-to-r from-primary via-accent to-primary origin-left"
            />
          </div>

          <div className="grid lg:grid-cols-3 gap-12 relative">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40 }}
                  animate={
                    inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }
                  }
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  className="relative"
                >
                  {/* Step number circle */}
                  <div className="w-20 h-20 rounded-full bg-gradient-primary flex items-center justify-center mx-auto mb-6 relative z-10 shadow-glow">
                    <span className="text-2xl font-heading font-bold text-primary-foreground">
                      {step.number}
                    </span>
                  </div>

                  <div className="text-center">
                    <div className="w-16 h-16 rounded-xl bg-card border border-border flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-8 h-8 text-primary" />
                    </div>
                    <h3 className="text-2xl font-heading font-bold mb-3">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
